import { useState } from "react";
import { ArrowLeft, UploadSimple, FileCsv, CheckCircle, XCircle, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

export default function BulkImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Önce bir CSV veya XLSX dosyası seçin.");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/certificates/bulk", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created_count} adet oluşturuldu, ${data.error_count} hata.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İçe aktarma başarısız.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="bulk-import-page">
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft size={14} /> Kayıt Defterine Dön
      </button>

      <div className="mt-6">
        <span className="label-eyebrow">/ Toplu İçe Aktar</span>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
          Çoklu sertifika oluşturma
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          CSV veya Excel (.xlsx) dosyası yükleyin — her satır bir sertifika olur. Her
          sertifika kendi numarası ve QR kodu ile doğrulanabilir hale gelir.
        </p>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-white border border-slate-200 p-6 lg:p-8">
          <div className="label-eyebrow">Adım 1 — Şablon</div>
          <p className="text-sm text-slate-600 mt-3">
            CSV şablonunu indirin, doldurun (Excel veya başka bir tabloyla CSV olarak
            kaydedin), sonra aşağıdan yükleyin.
          </p>
          <a
            data-testid="bulk-template-download"
            href={`${API_BASE}/certificates/template.csv`}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            <DownloadSimple size={14} weight="bold" /> CSV Şablonunu İndir
          </a>
          <div className="mt-6 text-xs text-slate-500 leading-relaxed">
            <div className="font-mono-plex text-[11px] mb-2">BEKLENEN SÜTUNLAR</div>
            <code className="block bg-slate-50 p-3 border border-slate-200 text-[11px] whitespace-pre-wrap break-words">
              holder_name, nationality, date_of_birth (YYYY-MM-DD), license_type,
              issue_date, expiry_date, notes, training_modules
            </code>
            <div className="mt-3 font-mono-plex text-[11px]">EĞİTİM MODÜLLERİ FORMATI</div>
            <code className="block bg-slate-50 p-3 border border-slate-200 text-[11px]">
              Kurs A:40:2026-01-10;Kurs B:16:2026-02-15
            </code>
            <p className="mt-2">Modüller arasında <b>;</b>, ad/saat/tarih arasında <b>:</b> kullanın.</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6 lg:p-8">
          <div className="label-eyebrow">Adım 2 — Yükle</div>
          <form onSubmit={onUpload} className="mt-5 space-y-4">
            <label
              htmlFor="bulk-file"
              className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-[#0B2046] cursor-pointer p-10 transition-colors"
              data-testid="bulk-file-dropzone"
            >
              {file ? (
                <>
                  <FileCsv size={32} className="text-[#0B2046]" />
                  <div className="font-mono-plex text-sm mt-3">{file.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <UploadSimple size={32} className="text-slate-400" />
                  <div className="text-sm text-slate-700 mt-3">CSV / XLSX seçmek için tıklayın</div>
                  <div className="text-xs text-slate-500 mt-1">veya sürükleyip bırakın</div>
                </>
              )}
              <input
                id="bulk-file"
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0] || null)}
                data-testid="bulk-file-input"
              />
            </label>
            <button
              type="submit"
              disabled={submitting || !file}
              data-testid="bulk-submit"
              className="w-full px-5 py-3 bg-[#0B2046] text-white font-medium disabled:opacity-50 hover:bg-[#113066]"
            >
              {submitting ? "İçe aktarılıyor…" : "Sertifikaları İçe Aktar"}
            </button>
          </form>
        </div>
      </div>

      {result && (
        <div className="mt-8 max-w-5xl" data-testid="bulk-result">
          <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-5">
              <div className="label-eyebrow">Oluşturuldu</div>
              <div className="font-display text-3xl font-black text-emerald-700 mt-2">
                {result.created_count}
              </div>
            </div>
            <div className="bg-white p-5">
              <div className="label-eyebrow">Hata</div>
              <div className="font-display text-3xl font-black text-red-700 mt-2">
                {result.error_count}
              </div>
            </div>
          </div>

          {result.created?.length > 0 && (
            <div className="mt-6 border border-slate-200 bg-white">
              <div className="bg-slate-50 px-4 py-2 label-eyebrow flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-700" /> Oluşturulanlar
              </div>
              <ul className="divide-y divide-slate-200 text-sm">
                {result.created.map((c) => (
                  <li key={c.certificate_number} className="px-4 py-2 flex justify-between">
                    <span>{c.holder_name}</span>
                    <span className="font-mono-plex">{c.certificate_number}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="mt-6 border border-red-200 bg-white">
              <div className="bg-red-50 px-4 py-2 label-eyebrow flex items-center gap-2 text-red-800">
                <XCircle size={14} /> Hatalar
              </div>
              <ul className="divide-y divide-red-100 text-sm">
                {result.errors.map((e, i) => (
                  <li key={i} className="px-4 py-2 flex justify-between text-red-800">
                    <span>Satır {e.row}</span>
                    <span className="font-mono-plex">{e.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
